import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { TranslocoDirective } from '@jsverse/transloco'
import { LoginFormController } from './login-form.controller'
import { LoginButtonComponent } from '../login-button/login-button.component'
import { RegisterButtonComponent } from '../register-button/register-button.component'
import { RegisterPromptComponent } from '../register-prompt/register-prompt.component'
import { BaseButtonComponent } from '../../../base/base-button/base-button.component'
import { BaseInputComponent } from '../../../base/base-input/base-input.component'
import { SiteConfigService } from '@/services/site-config.service'

/** Stacked login form: the login page and the auth modal. The header uses login-form-inline. */
@Component({
	selector: 'login-form',
	templateUrl: './login-form.component.html',
	styleUrl: './login-form.component.css',
	changeDetection: ChangeDetectionStrategy.Eager,
	host: { '[class]': 'surfaceClass()' },
	providers: [LoginFormController],
	imports: [
		TranslocoDirective,
		ReactiveFormsModule,
		BaseButtonComponent,
		BaseInputComponent,
		RegisterButtonComponent,
		RegisterPromptComponent,
		LoginButtonComponent
	]
})
export class LoginFormComponent {
	/** Light is the auth modal's surface; everywhere else the form sits on a dark page. */
	readonly surface = input<'dark' | 'light'>('dark')
	readonly redirectOnSuccess = input(true)

	readonly authenticated = output<boolean>()
	/** Left for the register page from the light surface's prompt, so the modal can close. */
	readonly registering = output<void>()

	protected readonly controller = inject(LoginFormController)
	protected readonly siteConfigService = inject(SiteConfigService)

	protected readonly surfaceClass = computed(() => `surface-${this.surface()}`)

	protected async handleSubmit(event: Event): Promise<void> {
		event.preventDefault()
		// A failed attempt shows its error in the form, so the modal holding it stays open.
		const result = await this.controller.submit(this.redirectOnSuccess(), true)
		if (result) {
			this.authenticated.emit(result)
		}
	}
}
